import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { CarouselProvider, Slider, Slide, ButtonBack, ButtonNext, WithStore } from 'pure-react-carousel'
import { colors } from '../../search-common/settings'
import 'pure-react-carousel/dist/react-carousel.es.css'

const CarouselWrapper = styled.div``

const Image = styled.img`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  object-fit: cover;
`

/**
 * Return the largest expected CSS source slot for each result card layout.
 *
 * @param {number} width Slide aspect-ratio width.
 * @param {number} height Slide aspect-ratio height.
 * @return {string}
 */
const getImageSizes = ( width, height ) => {
  if ( width > height ) {
    return '(max-width: 767px) 100vw, 690px'
  }

  return '(max-width: 767px) 100vw, 600px'
}

const ButtonBase = `
  position: absolute;
  background-color: ${colors.white};
  top: 50%;
  transform: translateY(-50%);
  border: none;
  padding: 4px;
  width: 40px;
  height: 40px;
  z-index: 100;
  &:hover {
    background-color: ${colors.green};
  }
  &:before,
  &:after {
    content: "";
    width: 6px;
    height: 6px;
    position: absolute;
    top: 50%,
    left: 50%;
    transform: translate(-50%, -50%);
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
  }
`

// eslint-disable-next-line space-in-parens
const Next = styled(ButtonNext)`
  ${ButtonBase};
  right: 0;
  &:after {
    border-left: 6px solid ${colors.black};
  }
`

// eslint-disable-next-line space-in-parens
const Back = styled(ButtonBack)`
  ${ButtonBase};
  left: 0;
  &:after {
    border-right: 6px solid ${colors.black};
  }
`

/**
 * CarouselImage
 */
const CarouselImage = ({ currentSlide, image, imageSizes, index, setImageLoaded }) => {
  const isCurrentSlide = index === currentSlide

  return (
    <Image
      className={isCurrentSlide ? 'lazyload' : null}
      data-srcset={isCurrentSlide ? image.srcset : null}
      data-sizes={isCurrentSlide ? imageSizes : null}
      onLoad={isCurrentSlide ? () => setImageLoaded( true ) : null}
      onError={isCurrentSlide ? () => setImageLoaded( true ) : null}
    />
  )
}

CarouselImage.propTypes = {
  currentSlide: PropTypes.number.isRequired,
  image: PropTypes.object.isRequired,
  imageSizes: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  setImageLoaded: PropTypes.func.isRequired,
}

const StoreCarouselImage = WithStore( CarouselImage, ( state ) => ({
  currentSlide: state.currentSlide,
}))

/**
 * SearchResultCarousel
 */
const SearchResultCarousel = ({ images, width, height, setImageLoaded }) => {
  const imageSizes = getImageSizes( width, height )

  return (
    <CarouselProvider
      naturalSlideWidth={width}
      naturalSlideHeight={height}
      totalSlides={images.length}
    >
      <CarouselWrapper>
        <Back />
        <Slider>
          {images.map(( image, index ) => (
            <Slide key={index} index={index}>
              <StoreCarouselImage
                image={image}
                imageSizes={imageSizes}
                index={index}
                setImageLoaded={setImageLoaded}
              />
            </Slide>
          ))}
        </Slider>
        <Next />
      </CarouselWrapper>
    </CarouselProvider>
  )
}

SearchResultCarousel.propTypes = {
  images: PropTypes.array.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  setImageLoaded: PropTypes.func.isRequired,
}

export default SearchResultCarousel
